import React from 'react';
import { Check, Plus, Star, Crown, Handshake } from 'lucide-react';
import { RARITIES, VARIANTS, MAX_LEVEL } from '../mock';

function LevelDots({ level, onChange, disabled }) {
  return (
    <div className="flex items-center gap-0.5 sm:gap-1">
      {Array.from({ length: MAX_LEVEL }).map((_, i) => {
        const filled = i < level;
        return (
          <button
            key={i}
            disabled={disabled}
            onClick={(e) => {
              e.stopPropagation();
              if (filled && i + 1 === level) onChange(level - 1);
              else onChange(i + 1);
            }}
            className={`h-5 w-5 sm:h-6 sm:w-6 rounded-md sm:rounded-lg flex items-center justify-center transition-transform hover:scale-110 border-2 ${
              filled
                ? 'bg-yellow-300 text-amber-900 border-[#3a1064] shadow-[0_2px_0_#3a1064]'
                : 'bg-white text-slate-300 border-slate-200 hover:border-purple-300'
            } ${disabled ? 'opacity-60 cursor-not-allowed' : ''}`}
            aria-label={`Set level ${i + 1}`}
          >
            <Star className="h-3 w-3 sm:h-3.5 sm:w-3.5" fill={filled ? 'currentColor' : 'none'} strokeWidth={2.5} />
          </button>
        );
      })}
    </div>
  );
}

export default function SpriteCard({ sprite, entry, onToggle, onLevel, onMaster, onTrade, onExpand }) {
  const rarity = RARITIES[sprite.rarity];
  const variant = VARIANTS[sprite.variant] || VARIANTS.base;
  const owned = !!entry?.owned;
  const level = entry?.level || 0;
  const mastered = !!entry?.mastered;
  const unreleased = !!sprite.unreleased;
  const isCheat = sprite.variant === 'cheat_master';

  return (
    <div
      data-testid="sprite-card"
      data-sprite-name={sprite.name}
      data-sprite-variant={sprite.variant}
      data-sprite-season={sprite.season || ''}
      data-sprite-unreleased={unreleased ? 'true' : 'false'}
      className={`group relative rounded-2xl overflow-hidden transition-all duration-200 border-[3px] hover:-translate-y-1 ${
        mastered
          ? 'border-[#ffb800] shadow-[0_6px_0_var(--t-card-border),0_0_24px_rgba(255,184,0,0.7)]'
          : owned
          ? 'shadow-[0_5px_0_var(--t-card-border)]'
          : 'shadow-[0_3px_0_var(--t-card-soft-shadow)]'
      } ${isCheat && !mastered ? 'sprite-card--cheat' : ''}`}
      style={{
        background: 'var(--t-card)',
        borderColor: mastered ? '#ffb800' : owned ? 'var(--t-card-border)' : 'var(--t-card-soft-border)',
      }}
    >
      {/* Top color bar */}
      <div
        className="h-2 w-full"
        style={{
          background: mastered
            ? 'linear-gradient(90deg,#ff2bd6,#ffd900,#00b8ff,#9d3dff,#2ee6c1,#ff2bd6)'
            : `linear-gradient(90deg, ${rarity.color}, ${variant.color})`,
          backgroundSize: mastered ? '300% 100%' : '100% 100%',
          animation: mastered ? 'rainbowSlide 4s linear infinite' : 'none',
        }}
      />

      {/* Image area */}
      <div
        className="relative aspect-square flex items-center justify-center sm:cursor-default cursor-zoom-in"
        onClick={() => onExpand && onExpand(sprite)}
        style={{
          background: mastered
            ? `radial-gradient(circle at center, #ffd90055 0%, transparent 65%), linear-gradient(135deg, #ffd900 0%, #ff2bd6 50%, #00b8ff 100%)`
            : isCheat
            ? `radial-gradient(circle at center, #39ff8855 0%, transparent 62%), linear-gradient(180deg, #062015 0%, #0a3a24 55%, #062015 100%)`
            : owned
            ? `radial-gradient(circle at center, ${rarity.color}44 0%, transparent 70%), linear-gradient(180deg, ${variant.color}33 0%, ${rarity.color}26 100%)`
            : `radial-gradient(circle at center, ${rarity.color}26 0%, transparent 70%), linear-gradient(180deg, ${variant.color}1a 0%, ${rarity.color}14 100%)`,
        }}
      >
        {isCheat && !mastered && <div className="cheat-scanlines" aria-hidden="true" />}
        {/* checkmark badge */}
        {owned && !mastered && (
          <div className="absolute top-1 right-1 sm:top-2 sm:right-2 h-5 w-5 sm:h-7 sm:w-7 rounded-full bg-emerald-500 text-white flex items-center justify-center shadow-[0_2px_0_#0c5132] ring-2 ring-white">
            <Check className="h-3 w-3 sm:h-4 sm:w-4" strokeWidth={3} />
          </div>
        )}
        {mastered && (
          <div className="absolute top-1 right-1 sm:top-2 sm:right-2 h-6 w-6 sm:h-8 sm:w-8 rounded-full bg-gradient-to-br from-yellow-300 to-amber-500 text-amber-900 flex items-center justify-center shadow-[0_2px_0_#7a4c00] ring-2 ring-white animate-pulse">
            <Crown className="h-3 w-3 sm:h-4 sm:w-4" strokeWidth={3} fill="currentColor" />
          </div>
        )}
        {unreleased && (
          <span className="absolute top-1 right-1 sm:top-2 sm:right-2 text-[8px] sm:text-[10px] font-black uppercase tracking-wider px-1.5 sm:px-2 py-0.5 rounded bg-[#ff2bd6] text-white border border-white/40 shadow-[0_2px_0_rgba(0,0,0,0.25)]">
            Unreleased
          </span>
        )}
        {/* variant tag */}
        <span
          className={`absolute top-1 left-1 sm:top-2 sm:left-2 text-[8px] sm:text-[10px] font-black uppercase tracking-wider px-1 sm:px-2 py-0.5 rounded text-white border border-white/40 shadow-[0_2px_0_rgba(0,0,0,0.25)] ${
            isCheat ? 'cheat-tag' : ''
          }`}
          style={{ background: variant.color }}
        >
          {variant.label}
        </span>

        {/* Mobile-only level badge (bottom-left) */}
        {level > 0 && (
          <span className="sm:hidden absolute bottom-1 left-1 text-[8px] font-black px-1 py-0.5 rounded bg-[#3a1064] text-yellow-300 border border-yellow-300 leading-none">
            Lv {level}/{MAX_LEVEL}
          </span>
        )}

        <img
          src={sprite.img}
          alt={sprite.name}
          loading="lazy"
          className={`h-[78%] w-[78%] object-contain transition-all duration-300 ${
            owned ? 'scale-100 drop-shadow-[0_6px_8px_rgba(58,16,100,0.35)]' : 'grayscale opacity-50 group-hover:opacity-80 group-hover:grayscale-0'
          } group-hover:scale-110 ${mastered ? 'drop-shadow-[0_0_18px_rgba(255,255,255,0.9)]' : ''}`}
          onError={(e) => { e.currentTarget.style.opacity = '0.3'; }}
        />
      </div>

      {/* Footer */}
      <div className="p-1.5 sm:p-3" style={{ background: 'var(--t-card)' }}>
        <button
          type="button"
          onClick={() => onExpand && onExpand(sprite)}
          className="min-w-0 w-full text-left sm:cursor-default cursor-zoom-in"
        >
          <div className="font-black text-[10px] leading-tight sm:text-sm sm:leading-normal truncate" style={{ color: 'var(--t-text)' }}>{sprite.name}</div>
          <div className="hidden sm:flex items-center gap-1.5 mt-0.5 flex-wrap">
            <span
              className="text-[10px] font-black uppercase px-1.5 py-0.5 rounded text-white"
              style={{ background: rarity.color }}
            >
              {rarity.label}
            </span>
            <span className="text-[11px] font-bold" style={{ color: 'var(--t-text-soft)' }}>{sprite.drop}</span>
            {sprite.tag && (
              <span className="text-[10px] font-black uppercase px-1.5 py-0.5 rounded bg-cyan-400 text-white">
                {sprite.tag}
              </span>
            )}
          </div>
        </button>

        {/* Level row — desktop only */}
        {!unreleased && (
        <div className="hidden sm:flex mt-3 items-center justify-between">
          <LevelDots level={level} onChange={(lvl) => onLevel(sprite.id, lvl)} />
          <span className="text-xs font-black" style={{ color: 'var(--t-text)' }}>Lv {level}/{MAX_LEVEL}</span>
        </div>
        )}

        {unreleased ? (
          <div className="mt-1.5 sm:mt-3">
            <div
              data-testid="unreleased-badge"
              className="w-full h-8 sm:h-9 rounded-md sm:rounded-xl text-[10px] sm:text-[11px] font-black uppercase tracking-[2px] flex items-center justify-center border-2 bg-[#ff2bd6] text-white shadow-[0_2px_0_var(--t-card-border)] sm:shadow-[0_3px_0_var(--t-card-border)]"
              style={{ borderColor: 'var(--t-card-border)' }}
            >
              Unreleased
            </div>
          </div>
        ) : (
        <>
        {/* Owned + Mastered buttons */}
        <div className="mt-1.5 sm:mt-3 grid grid-cols-2 gap-1 sm:gap-2">
          <button
            onClick={() => onToggle(sprite.id)}
            aria-label={owned ? 'Mark as not owned' : 'Mark as owned'}
            title={owned ? 'Owned ✓' : 'Mark as Owned'}
            className={`h-7 sm:h-9 rounded-md sm:rounded-xl text-[9px] sm:text-[11px] leading-none font-black uppercase tracking-normal sm:tracking-wide transition-all flex items-center justify-center gap-1 border-2 shadow-[0_2px_0_var(--t-card-border)] sm:shadow-[0_3px_0_var(--t-card-border)] px-1 min-w-0 overflow-hidden ${
              owned
                ? 'bg-emerald-500 hover:bg-emerald-600 text-white'
                : 'hover:opacity-90'
            }`}
            style={{
              borderColor: 'var(--t-card-border)',
              background: owned ? undefined : 'var(--t-card)',
              color: owned ? undefined : 'var(--t-text)',
            }}
          >
            {owned ? (
              <>
                <Check className="h-3 w-3 sm:h-3.5 sm:w-3.5 shrink-0" strokeWidth={3} />
                <span className="truncate">Owned</span>
              </>
            ) : (
              <>
                <Plus className="h-3 w-3 sm:h-3.5 sm:w-3.5 shrink-0" strokeWidth={3} />
                <span className="truncate">Owned</span>
              </>
            )}
          </button>
          <button
            onClick={() => onMaster(sprite.id)}
            aria-label={mastered ? 'Unmaster' : 'Mark as mastered'}
            title={mastered ? 'Mastered 👑' : 'Mark as Mastered'}
            className={`h-7 sm:h-9 rounded-md sm:rounded-xl text-[9px] sm:text-[11px] leading-none font-black uppercase tracking-normal sm:tracking-wide transition-all flex items-center justify-center gap-1 border-2 shadow-[0_2px_0_var(--t-card-border)] sm:shadow-[0_3px_0_var(--t-card-border)] px-1 min-w-0 overflow-hidden ${
              mastered
                ? 'bg-gradient-to-r from-yellow-300 via-pink-400 to-cyan-400 text-[#3a1064]'
                : 'hover:opacity-90'
            }`}
            style={{
              borderColor: 'var(--t-card-border)',
              background: mastered ? undefined : 'var(--t-card)',
              color: mastered ? undefined : 'var(--t-text)',
            }}
          >
            <Crown className="h-3 w-3 sm:h-3.5 sm:w-3.5 shrink-0" strokeWidth={3} fill={mastered ? 'currentColor' : 'none'} />
            <span className="truncate">Mastered</span>
          </button>
        </div>

        {/* Trade button */}
        <button
          onClick={() => onTrade && onTrade(sprite)}
          aria-label="Propose a trade"
          title="Propose a Trade"
          className="mt-1.5 sm:mt-2 w-full h-7 sm:h-9 rounded-md sm:rounded-xl text-[10px] sm:text-[11px] font-black uppercase tracking-wide transition-all flex items-center justify-center gap-1 sm:gap-1.5 border-2 bg-[#ff2bd6] hover:bg-[#e119bf] text-white shadow-[0_2px_0_var(--t-card-border)] sm:shadow-[0_3px_0_var(--t-card-border)]"
          style={{ borderColor: 'var(--t-card-border)' }}
        >
          <Handshake className="h-3.5 w-3.5 shrink-0" strokeWidth={3} />
          <span className="hidden sm:inline">Trade</span>
          <span className="sm:hidden">Trade</span>
        </button>
        </>
        )}
      </div>
    </div>
  );
}
